import React from 'react';
import { PieChart, TrendingUp, BarChart3 } from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from 'recharts';
import CollapsibleSection from '../ui/CollapsibleSection';

const PRODUCT_TYPES = {
  honey: { label: 'Honey', icon: '🍯', color: '#f59e0b', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  nuc: { label: 'Nuc', icon: '📦', color: '#8b5cf6', bgColor: 'bg-violet-50', textColor: 'text-violet-700' },
  queen: { label: 'Queen', icon: '👑', color: '#ec4899', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  bee_package: { label: 'Bee Package', icon: '🐝', color: '#14b8a6', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  hive: { label: 'Hive', icon: '🏠', color: '#f97316', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  equipment: { label: 'Equipment', icon: '🔧', color: '#6b7280', bgColor: 'bg-stone-50', textColor: 'text-stone-700' },
};

const SalesCharts = ({ summary, pieChartData, monthlyChartData, yardChartData }) => (
  <div className="space-y-3">
    {/* Revenue by Product Type */}
    <CollapsibleSection title="Revenue by Product Type" icon={PieChart} defaultOpen={true}
      badge={`${Object.keys(summary?.by_product_type || {}).length} types`} badgeColor="green">
      <div className="pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pieChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value"
                    label={({ name, value }) => `${name}: $${value.toFixed(0)}`}>
                    {pieChartData.map((entry, index) => (<Cell key={`cell-${entry.name}`} fill={entry.color} />))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-stone-400">No sales data yet</div>
          )}
          <div className="space-y-2">
            {Object.entries(PRODUCT_TYPES).map(([type, config]) => {
              const data = summary?.by_product_type?.[type] || { count: 0, revenue: 0 };
              return (
                <div key={type} className={`flex items-center justify-between p-3 rounded-lg ${config.bgColor}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{config.icon}</span>
                    <span className={`font-medium ${config.textColor}`}>{config.label}</span>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold ${config.textColor}`}>${data.revenue.toFixed(2)}</div>
                    <div className="text-xs text-stone-500">{data.count} sales</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </CollapsibleSection>

    {/* Revenue Over Time */}
    <CollapsibleSection title="Revenue Over Time" icon={TrendingUp} defaultOpen={monthlyChartData.length > 0}
      badge={monthlyChartData.length > 0 ? `${monthlyChartData.length} months` : 'No data'} badgeColor="amber">
      <div className="pt-4">
        {monthlyChartData.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value, name) => [name === 'revenue' ? `$${value.toFixed(2)}` : value, name === 'revenue' ? 'Revenue' : 'Sales']} />
                <Legend />
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                <Line type="monotone" dataKey="count" name="Sales Count" stroke="#6b7280" strokeWidth={1} dot={{ fill: '#6b7280' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-stone-400">No monthly data yet - record sales to see trends</div>
        )}
      </div>
    </CollapsibleSection>

    {/* Revenue by Apiary */}
    <CollapsibleSection title="Revenue by Apiary" icon={BarChart3} defaultOpen={false}
      badge={`${Object.keys(summary?.by_yard || {}).length} apiaries`} badgeColor="violet">
      <div className="pt-4">
        {yardChartData.length > 0 ? (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yardChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-stone-400">No apiary data yet</div>
        )}
      </div>
    </CollapsibleSection>
  </div>
);

export default SalesCharts;
